import {
  ActionButtonComponent,
  AvatarComponent,
  type ActionButtonColor,
  type ActionButtonType,
} from '@myre/ui-primitive-components';
import {
  TranslatePipe,
  UnsubscribeOnDestroy,
  UploadCanceled,
  UploadEnd,
  UploadError,
  UploadProgress,
  UploadService,
  UploadStart,
  type Upload,
  type UploadEvent,
} from '@myre/ui-primitive-core';

import { PercentPipe } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostBinding,
  inject,
  Input,
  Output,
  ViewChild,
  type OnDestroy,
} from '@angular/core';
import { NG_VALUE_ACCESSOR, type ControlValueAccessor } from '@angular/forms';
import { type Subscription } from 'rxjs';

export type Method = 'POST' | 'GET' | 'PUT';

export const UPLOAD_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => UploadComponent),
  multi: true,
};

@Component({
  selector: 'myre-upload',
  templateUrl: './upload.component.html',
  styleUrls: ['./upload.component.scss'],
  providers: [UPLOAD_VALUE_ACCESSOR],
  imports: [AvatarComponent, ActionButtonComponent, PercentPipe, TranslatePipe],
})
export class UploadComponent<T = string>
  implements OnDestroy, ControlValueAccessor
{
  private _changeDetectionRef = inject(ChangeDetectorRef);
  private _uploadService = inject(UploadService);

  @Input()
  public readonly: boolean = false;

  @Input()
  public iconName: string;

  @Input()
  public label: string = '_library.import.file';

  @Input()
  public type: ActionButtonType = 'primary';

  @Input()
  public color: ActionButtonColor = 'blue';

  @Input()
  public adaptText: boolean = true;

  @Input()
  public reverse: boolean = true;

  @Input()
  public withAvatar: boolean = false;

  @Input()
  public avatarSize: number = 100;

  @Input()
  public id: string;

  @Input()
  public required: boolean = false;

  @Input()
  public url: string | ((file?: File) => Promise<string>);

  @ViewChild('fileinput', { static: false })
  public fileinput: ElementRef;

  @Input()
  public name: string = '';

  @Input()
  public onUploadCallback: () => void;

  @Input()
  public authorization: string;

  @Input()
  public method: Method = 'PUT';

  @Input()
  public accept: string;

  @Input()
  public auto: boolean = true;

  @Input()
  public maxFileSize: number;

  @Input()
  public includeContentTypeHeader: boolean = false;

  @Input()
  public design: 'area' | 'button' = 'area';

  @Input()
  public loading: boolean = false;

  @Input()
  public keepUploadingOnDestroy: boolean = false;

  @Input()
  public bodyFormatter: (files: File) => any;

  @Input()
  public preview: boolean = false;

  @Input()
  public clearOnEnd: boolean = true;

  @Input()
  public getPreviewUrl: (a: T) => string = a => a as string;

  public disabled: boolean = false;

  @Output()
  public onSelect = new EventEmitter<File>();

  @Output()
  public onUpload = new EventEmitter<{
    file: File;
    body: any;
  }>();

  @Output()
  public onBeforeSend = new EventEmitter<File>();

  @Output()
  public onUploadStart = new EventEmitter<Upload>();

  @Output()
  public onError = new EventEmitter<any>();

  @HostBinding('class.inline-avatar')
  protected get _isInlineAvatar(): boolean {
    return this.withAvatar && !this.adaptText;
  }

  protected get _uploading(): boolean {
    return typeof this._uploadId === 'number';
  }

  protected _value: T | null = null;
  protected _progress: number = 0;
  protected _file: File = null;
  private _destroyed: boolean;
  private _uploadId: number;

  @UnsubscribeOnDestroy()
  private _subscriptions: Subscription[] = [];

  @Input()
  public set uploadId(uploadId: number) {
    if (this._uploadId === uploadId) {
      return;
    }

    const obs = this._uploadService.getUploadObservable(uploadId);

    if (obs) {
      this._uploadId = uploadId;

      this._subscriptions.push(
        obs.subscribe(event => {
          this._eventHandler(event);
        }),
      );
    }
  }

  public ngOnDestroy(): void {
    this._destroyed = true;

    if (!this._uploadId) {
      return;
    }

    if (this.keepUploadingOnDestroy) {
      this._uploadService.showInQueue(this._uploadId);
    } else {
      this._uploadService.cancel(this._uploadId);
    }
  }

  public async upload(): Promise<void> {
    if (!this._file) {
      return;
    }

    const url =
      typeof this.url === 'function' ? await this.url(this._file) : this.url;

    this.onBeforeSend.emit(this._file);

    this._subscriptions.push(
      this._uploadService
        .upload(
          [this._file],
          this.method,
          this.name,
          url,
          this.authorization,
          this.includeContentTypeHeader,
          this.bodyFormatter && (files => this.bodyFormatter(files[0])),
          this.onUploadCallback,
        )
        .subscribe({
          next: event => {
            this._eventHandler(event);
          },
          error: (err: any) => {
            if (this.onError) {
              return this.onError.emit(err);
            }
          },
        }),
    );
  }

  public writeValue(value: T): void {
    this._value = value;
  }

  public registerOnChange(fn: (value: T) => any): void {
    this._onModelChange = fn;
  }

  public registerOnTouched(fn: () => any): void {
    this._onTouched = fn;
  }

  public setDisabledState(value: boolean): void {
    this.disabled = value;
  }

  public onChange(value: T): void {
    this._onModelChange(value);
  }

  public onBlur(): void {
    this._onTouched();
  }

  protected _getUrl(): string {
    if (this._file) {
      return URL.createObjectURL(this._file);
    }

    return encodeURI(this.getPreviewUrl(this._value));
  }

  protected _remove(): void {
    this._value = null;
    this.onChange(this._value);
  }

  protected _onFileSelect(event: any): void {
    this._file = (event.dataTransfer || event.target).files[0];

    this.onSelect.emit(this._file);

    if (this._file && this.auto) {
      void this.upload();
    }

    this._clearInputElement();
  }

  protected _cancel(): void {
    this._uploadService.cancel(this._uploadId);
    this._clear();
  }

  private _onModelChange: (value: T) => any = () => {};

  private _onTouched: () => any = () => {};

  private _eventHandler(event: UploadEvent): void {
    if (event instanceof UploadEnd) {
      this.onUpload.emit({
        file: event.files[0],
        body: event.body,
      });

      this.onChange(this._value);

      if (this.clearOnEnd) {
        this._clear();
      }
    }

    if (event instanceof UploadStart) {
      this._uploadId = event.uploadId;
      this.onUploadStart.emit(this._uploadService.getUpload(event.uploadId));
    }

    if (event instanceof UploadProgress) {
      this._progress = event.progress;
    }

    if (event instanceof UploadError) {
      this.onError.emit(event.error);
      this._clear();
    }

    if (event instanceof UploadCanceled) {
      this._clear();
    }

    if (!this._destroyed) {
      this._changeDetectionRef.detectChanges();
    }
  }

  private _clear(): void {
    this._uploadId = null;
    this._file = null;
    this._progress = 0;
    this._clearInputElement();
  }

  private _clearInputElement(): void {
    if (this.fileinput && this.fileinput.nativeElement) {
      this.fileinput.nativeElement.value = '';
    }
  }
}
